import React, { useEffect, useState } from 'react'
import { Button, Card, DatePicker, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { get, patch, post, del } from '../../lib/request'
import { ConfigItem, RuleVersion, errMsg, fmtDate, fmtTime } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { hasPermission } from '../../utils/permissions'
import MpBannerListField, { MP_BANNER_MAX, type MpBanner, parseMpBanner, validateMpBanners } from './MpBannerListField'

const { Text } = Typography

/**
 * richtext_fonts_v1（2026-09-15）：把 quill 默认的 class 输出（.ql-font-serif / .ql-size-large /
 * .ql-color-* 等）切换为 inline style 输出（style="font-family:serif" / "font-size:18px" /
 * "color:#f00"）。小程序 rich-text 组件只认 inline style、不认 quill 的 class，故必须切到
 * style attributor，后台配置的字体/字号/颜色才能随 HTML 透传到小程序端。
 * 注册幂等（模块顶层执行一次，ReactQuill.Quill 为单例）。
 */
const Quill = ReactQuill.Quill
const FontStyle = Quill.import('attributors/style/font') as { whitelist?: string[] }
const SizeStyle = Quill.import('attributors/style/size') as { whitelist?: string[] }
const ColorStyle = Quill.import('attributors/style/color')
const BackgroundStyle = Quill.import('attributors/style/background')
// 字体白名单收敛为 CSS 通用族名（小程序可识别，避免具体字体名在 rich-text 端无效回落）
FontStyle.whitelist = ['sans-serif', 'serif', 'monospace']
// 字号白名单用具体 px（输出 inline font-size:NNpx），小程序可正确渲染
SizeStyle.whitelist = ['12px', '14px', '16px', '18px', '20px', '24px']
Quill.register(FontStyle, true)
Quill.register(SizeStyle, true)
Quill.register(ColorStyle, true)
Quill.register(BackgroundStyle, true)

/**
 * rules_richtext_v2：《转介答谢规则》正文富文本编辑器（所见即所得）。
 * 从 Word/网页粘贴可保留字体/加粗/颜色/字号；输出 HTML 字符串供小程序 rich-text 渲染。
 * toolbar 限定白名单（字体/粗体/斜体/下划线/颜色/背景/字号/标题/列表/清除格式），
 * 与小程序端 HTML→nodes 转换器支持的标签子集对齐，避免产生小程序不识别的结构。
 */
const RICH_TOOLBAR = [
  [{ font: ['sans-serif', 'serif', 'monospace'] }],
  ['bold', 'italic', 'underline'],
  [{ color: [] }, { background: [] }],
  [{ size: ['12px', '14px', '16px', '18px', '20px', '24px'] }],
  [{ header: [1, 2, 3, false] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['clean'],
]

interface RichRuleEditorProps {
  value?: string
  onChange?: (html: string) => void
  placeholder?: string
}
const RichRuleEditor: React.FC<RichRuleEditorProps> = ({ value, onChange, placeholder }) => (
  <ReactQuill
    theme="snow"
    value={value || ''}
    onChange={(html: string) => onChange?.(html)}
    placeholder={placeholder}
    // richtext_fonts_v1：clipboard.matchVisual 由 false 改 true——粘贴 Word/网页内容时
    // 保留字体/字号/颜色等可视样式（配合上方 style attributor，输出为 inline style）。
    modules={{ toolbar: RICH_TOOLBAR, clipboard: { matchVisual: true } }}
    formats={['font', 'bold', 'italic', 'underline', 'color', 'background', 'size', 'header', 'list']}
    style={{ background: '#fff', borderRadius: 6 }}
  />
)

/**
 * 答谢金额核算规则（旧口径）+ 小程序内容配置 + 《转介答谢规则》版本管理
 * reward_rule 三档枚举（Contract Pack 逐字）：full_first_month / fixed / ratio
 * 小程序内容：mp_banner / mp_privacy_text / mp_privacy_text_en
 *   liuwei_b1（2026-10-14）：mp_banner 由单个 URL 改为 MpBanner[]（最多 MP_BANNER_MAX 张，JSON 数组存储；旧单 URL 兼容读取为一行）
 * rules_versioning_v1：答谢规则版本区——中英两版正文 + 生效日期，可发布新版替换、历史版保留、可手动删除；
 *   原 mp_rules_text（推荐规则文案）已废弃移除（小程序从不读它，现由 rule_versions 表驱动）。
 */
const RewardConfigTab: React.FC = () => {
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = hasPermission(role, 'CONFIG_EDIT')
  const canEditRule = hasPermission(role, 'BUSINESS_CONFIG_EDIT')

  const [loading, setLoading] = useState(true)
  const [savingRule, setSavingRule] = useState(false)
  const [savingMp, setSavingMp] = useState(false)
  const [ruleForm] = Form.useForm<{ reward_rule: string; reward_fixed_amount?: number; reward_ratio?: number }>()
  const [mpForm] = Form.useForm<{ mp_banner?: MpBanner[]; mp_privacy_text?: string; mp_privacy_text_en?: string }>()
  const ruleValue = Form.useWatch('reward_rule', ruleForm)

  // ---- 答谢规则版本管理 ----
  const [ruleVersions, setRuleVersions] = useState<RuleVersion[]>([])
  const [rulesLoading, setRulesLoading] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [newRuleForm] = Form.useForm<{ contentZh: string; contentEn?: string; effectiveAt: Dayjs; effectiveEnd?: Dayjs }>()
  // rule_history_detail_v1（2026-09-15）：历史版本「查看详情」弹窗当前展示的版本（null=关闭）
  const [detailVersion, setDetailVersion] = useState<RuleVersion | null>(null)

  useEffect(() => {
    get<ConfigItem[]>('/admin/configs')
      .then((items) => {
        const map = new Map(items.map((c) => [c.key, c.value]))
        ruleForm.setFieldsValue({
          reward_rule: map.get('reward_rule') ?? 'full_first_month',
          reward_fixed_amount: map.get('reward_fixed_amount') ? Number(map.get('reward_fixed_amount')) : undefined,
          reward_ratio: map.get('reward_ratio') ? Number(map.get('reward_ratio')) : undefined,
        })
        mpForm.setFieldsValue({
          mp_banner: parseMpBanner(map.get('mp_banner')),
          mp_privacy_text: map.get('mp_privacy_text') ?? '',
          mp_privacy_text_en: map.get('mp_privacy_text_en') ?? '',
        })
      })
      .catch((err) => message.warning(errMsg(err, '配置加载失败')))
      .finally(() => setLoading(false))
    fetchRuleVersions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleForm, mpForm])

  const fetchRuleVersions = async () => {
    setRulesLoading(true)
    try {
      const items = await get<RuleVersion[]>('/admin/rules')
      setRuleVersions(items)
    } catch (err) {
      message.warning(errMsg(err, '规则版本加载失败'))
    } finally {
      setRulesLoading(false)
    }
  }

  const saveRule = async () => {
    const v = await ruleForm.validateFields()
    setSavingRule(true)
    try {
      await patch('/admin/configs', {
        reward_rule: v.reward_rule,
        ...(v.reward_fixed_amount != null ? { reward_fixed_amount: v.reward_fixed_amount } : {}),
        ...(v.reward_ratio != null ? { reward_ratio: v.reward_ratio } : {}),
      })
      message.success('核算规则已保存')
    } catch (err: unknown) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSavingRule(false)
    }
  }

  const saveMp = async () => {
    let v: { mp_banner?: MpBanner[]; mp_privacy_text?: string; mp_privacy_text_en?: string }
    try {
      v = await mpForm.validateFields()
    } catch (err: unknown) {
      // liuwei_b1：横幅行缺图等校验失败时给出明确提示（字段下方同时显示具体第几张）
      const first = (err as { errorFields?: { errors?: string[] }[] })?.errorFields?.[0]?.errors?.[0]
      message.error(first ?? '请检查小程序内容配置')
      return
    }
    setSavingMp(true)
    try {
      await patch('/admin/configs', {
        mp_banner: v.mp_banner ?? [],
        mp_privacy_text: v.mp_privacy_text ?? '',
        mp_privacy_text_en: v.mp_privacy_text_en ?? '',
      })
      message.success('小程序内容配置已保存')
    } catch (err: unknown) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSavingMp(false)
    }
  }

  const publishRule = async () => {
    const v = await newRuleForm.validateFields()
    setPublishing(true)
    try {
      await post('/admin/rules', {
        contentZh: v.contentZh,
        contentEn: v.contentEn?.trim() ? v.contentEn : null,
        effectiveAt: v.effectiveAt.format('YYYY-MM-DD'),
        // share_reward_rule_end_v1：结束时间（纯展示，可空；留空 = 长期有效，显式传 null）
        effectiveEnd: v.effectiveEnd ? v.effectiveEnd.format('YYYY-MM-DD') : null,
      })
      message.success('新版规则已发布，小程序即刻生效')
      newRuleForm.resetFields()
      fetchRuleVersions()
    } catch (err: unknown) {
      message.error(errMsg(err, '发布失败'))
    } finally {
      setPublishing(false)
    }
  }

  const deleteRule = async (id: number) => {
    try {
      await del(`/admin/rules/${id}`)
      message.success('已删除')
      fetchRuleVersions()
    } catch (err: unknown) {
      message.error(errMsg(err, '删除失败'))
    }
  }

  const versionColumns = [
    {
      // share_reward_rule_end_v1：生效期间 = 生效日期 ~ 结束时间（无结束时间 = 长期）
      title: '生效期间',
      dataIndex: 'effectiveAt',
      key: 'effectiveAt',
      width: 210,
      render: (v: string, r: RuleVersion) => `${fmtDate(v)} ~ ${r.effectiveEnd ? fmtDate(r.effectiveEnd) : '长期'}`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 96,
      render: (v: string) =>
        v === 'published' ? <Tag color="green">当前生效</Tag> : <Tag>历史版本</Tag>,
    },
    {
      title: '中文正文（摘要）',
      dataIndex: 'contentZh',
      key: 'contentZh',
      ellipsis: true,
      render: (v: string) => (v && v.length > 60 ? `${v.slice(0, 60)}…` : v),
    },
    {
      title: '英文',
      dataIndex: 'contentEn',
      key: 'contentEn',
      width: 70,
      render: (v: string | null) => (v && v.trim() ? '有' : '—'),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (v: string) => fmtTime(v),
    },
    {
      // rule_history_detail_v1：查看该版中英全文（含样式），published / 历史版均可查看
      title: '详情',
      key: 'detail',
      width: 70,
      render: (_: unknown, r: RuleVersion) => (
        <Button size="small" type="link" style={{ padding: 0, minHeight: 32 }} onClick={() => setDetailVersion(r)}>
          查看
        </Button>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      render: (_: unknown, r: RuleVersion) =>
        r.status === 'published' ? (
          <Text type="secondary" style={{ fontSize: 12 }}>生效版禁删</Text>
        ) : (
          <Popconfirm title="删除该历史版本？" okText="删除" cancelText="取消" onConfirm={() => deleteRule(r.id)}>
            <Button size="small" danger style={{ minHeight: 32 }} disabled={!canEditRule}>
              删除
            </Button>
          </Popconfirm>
        ),
    },
  ]

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {/* 原「奖励规则配置」改名弱化：该卡仍在做现金核算（computeRewardAmount），仅弱化命名避免与「答谢方案」混淆 */}
      <Card title="答谢金额核算规则（旧口径）" style={{ borderRadius: 12, maxWidth: 640 }} loading={loading}>
        <Form form={ruleForm} layout="vertical" disabled={!canEdit}>
          <Form.Item name="reward_rule" label="核算规则" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'full_first_month', label: '全额首月租金（默认）' },
                { value: 'fixed', label: '固定金额' },
                { value: 'ratio', label: '按首月租金比例' },
              ]}
            />
          </Form.Item>
          {ruleValue === 'fixed' && (
            <Form.Item
              name="reward_fixed_amount"
              label="固定奖励金额（元）"
              rules={[{ required: true, message: '固定金额必填' }]}
            >
              <InputNumber min={0} style={{ width: '100%', minHeight: 44 }} />
            </Form.Item>
          )}
          {ruleValue === 'ratio' && (
            <Form.Item
              name="reward_ratio"
              label="奖励比例（0~1，例：0.5 = 首月租金的 50%）"
              rules={[{ required: true, message: '比例必填' }]}
            >
              <InputNumber min={0} max={1} step={0.05} style={{ width: '100%', minHeight: 44 }} />
            </Form.Item>
          )}
          {canEdit && (
            <Button type="primary" loading={savingRule} onClick={saveRule} style={{ minHeight: 44 }}>
              保存核算规则
            </Button>
          )}
        </Form>
        <Text type="secondary" style={{ fontSize: 12 }}>
          一键核算按此规则生成默认金额；财务经理可在「答谢管理」逐笔调整（强制原因留痕）。答谢方案 / 礼遇文案请在「答谢方案」维护。
        </Text>
      </Card>

      <Card title="小程序内容配置" style={{ borderRadius: 12, maxWidth: 640 }} loading={loading}>
        <Form form={mpForm} layout="vertical" disabled={!canEdit}>
          <Form.Item name="mp_banner" label={`首页横幅（最多 ${MP_BANNER_MAX} 张）`} rules={[{ validator: validateMpBanners }]}>
            <MpBannerListField />
          </Form.Item>
          <Form.Item name="mp_privacy_text" label="隐私协议文案">
            <Input.TextArea rows={4} placeholder="留资表单提交前的勾选协议内容" />
          </Form.Item>
          <Form.Item name="mp_privacy_text_en" label="隐私协议文案（英文版 · 选填）">
            <Input.TextArea rows={4} placeholder="留空则英文模式仍显示中文版" />
          </Form.Item>
          {canEdit && (
            <Button type="primary" loading={savingMp} onClick={saveMp} style={{ minHeight: 44 }}>
              保存小程序内容
            </Button>
          )}
        </Form>
        <Text type="secondary" style={{ fontSize: 12 }}>
          《转介答谢规则》正文请用下方「答谢规则版本」管理（原「推荐规则文案」框已废弃：小程序从未读取它）。
        </Text>
      </Card>

      {/* rules_versioning_v1：《转介答谢规则》版本管理 */}
      <Card title="答谢规则版本" style={{ borderRadius: 12 }} loading={rulesLoading && ruleVersions.length === 0}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
          填写中英文两版《转介答谢规则》正文并「发布新版」后，小程序规则页立即显示新内容；替换下来的旧版本保留在下方历史列表（含生效日期），可手动删除。英文选填，留空则英文模式显示中文版。
        </Text>

        {canEditRule && (
          <Form form={newRuleForm} layout="vertical" style={{ maxWidth: 640, marginBottom: 24, padding: 16, background: '#fafafa', borderRadius: 8 }}>
            <Form.Item name="contentZh" label="规则正文（中文）" rules={[{ required: true, message: '中文正文必填' }]}>
              <RichRuleEditor placeholder="《转介答谢规则》中文版正文（可直接从 Word/网页粘贴，保留加粗/颜色/字号）" />
            </Form.Item>
            <Form.Item name="contentEn" label="规则正文（英文 · 选填）">
              <RichRuleEditor placeholder="English version (optional; leave empty to fall back to Chinese)" />
            </Form.Item>
            <Form.Item name="effectiveAt" label="生效日期" rules={[{ required: true, message: '生效日期必填' }]} initialValue={dayjs()}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            {/* share_reward_rule_end_v1：结束时间（纯展示，不影响状态机；日粒度，留空表示长期有效） */}
            <Form.Item name="effectiveEnd" label="结束时间（选填 · 留空表示长期有效）">
              <DatePicker style={{ width: '100%' }} placeholder="不选则长期有效" />
            </Form.Item>
            <Button type="primary" loading={publishing} onClick={publishRule} style={{ minHeight: 44 }}>
              发布新版
            </Button>
          </Form>
        )}

        <Table<RuleVersion>
          rowKey="id"
          size="small"
          loading={rulesLoading}
          columns={versionColumns}
          dataSource={ruleVersions}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: '暂无规则版本，请在上方发布第一版' }}
        />
      </Card>

      {/* rule_history_detail_v1：历史版本详情弹窗——显示该版中英全文（含样式），所见即所得。
          内容为后台富文本编辑器产出、结构可控；后端已校验，可信内网渲染。 */}
      <Modal
        open={!!detailVersion}
        onCancel={() => setDetailVersion(null)}
        footer={null}
        width={760}
        title={
          detailVersion
            ? `规则详情 · 生效日期 ${fmtDate(detailVersion.effectiveAt)} · ${detailVersion.status === 'published' ? '当前生效' : '历史版本'}`
            : ''
        }
      >
        {detailVersion && (
          <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>中文正文</Text>
            <div
              style={{ border: '1px solid #E8ECF0', borderRadius: 8, padding: 16, marginBottom: 16, background: '#fff' }}
              dangerouslySetInnerHTML={{ __html: detailVersion.contentZh || '<span style="color:#999">（空）</span>' }}
            />
            {detailVersion.contentEn && detailVersion.contentEn.trim() ? (
              <>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>英文正文</Text>
                <div
                  style={{ border: '1px solid #E8ECF0', borderRadius: 8, padding: 16, background: '#fff' }}
                  dangerouslySetInnerHTML={{ __html: detailVersion.contentEn }}
                />
              </>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>（无英文版，英文模式回落中文）</Text>
            )}
          </div>
        )}
      </Modal>
    </Space>
  )
}

export default RewardConfigTab
